/**
 * Referral manager — invite-a-friend, RPC + URL helpers.
 *
 * The DB schema lives in supabase/migrations/20260521120000_referral_system.sql.
 * This module is the JS-side surface:
 *
 *   getOrCreateMyCode()      → fetches the caller's referral code; mints one
 *                              on first call. Cached in-memory for the session.
 *   claimReferralCode(code)  → invitee writes a pending event; chips grant
 *                              when they save their first real game session
 *                              (DB trigger handles that completion path).
 *   getMySummary()           → rollup of total invited / granted / chips
 *                              earned. For the "Invite friends" modal.
 *   buildShareUrl(code)      → canonical /i/<code> short URL.
 *   extractCodeFromUrl(url)  → parses ?ref=<code> or /i/<code> back out.
 *
 * Anti-abuse on the client side:
 *   - claimReferralCode is idempotent against re-runs (DB unique constraint
 *     on invitee_user_id). Multiple calls return the same outcome.
 *   - We store a `pendingCode` in localStorage as a guard against
 *     race conditions during sign-up — claim is retried at the next
 *     opportunity if it fails on first attempt.
 *   - device_fingerprint is best-effort (Capacitor Device plugin would
 *     give a stable ID; we fall back to a random localStorage UUID).
 */

import { supabase } from '@/lib/supabase/client';

const MARKETING_URL = 'https://pokerblitzgame.com';
const STORAGE_PENDING_CODE = 'referral_pending_code';
const STORAGE_DEVICE_FINGERPRINT = 'referral_device_fingerprint';

export interface ReferralSummary {
  user_id: string;
  total_invited: number;
  total_granted: number;
  total_pending: number;
  total_chips_earned: number;
  last_granted_at: string | null;
}

export interface ClaimResult {
  success: boolean;
  reason?:
    | 'empty_code'
    | 'invalid_code'
    | 'self_referral'
    | 'inviter_cap_reached'
    | 'invitee_already_claimed'
    | 'rpc_error';
  inviterUserId?: string;
  note?: string;
}

let cachedMyCode: string | null = null;

/** Returns the caller's referral code, generating one on first request. */
export async function getOrCreateMyCode(): Promise<string | null> {
  if (cachedMyCode) return cachedMyCode;
  const { data, error } = await supabase.rpc('get_or_create_referral_code');
  if (error) {
    console.warn('[referral] get_or_create_referral_code failed', error);
    return null;
  }
  cachedMyCode = (data as string) ?? null;
  return cachedMyCode;
}

/** Invitee path. Writes a pending event; trigger grants chips on first game. */
export async function claimReferralCode(code: string): Promise<ClaimResult> {
  if (!code || !code.trim()) return { success: false, reason: 'empty_code' };

  const fingerprint = getOrMintDeviceFingerprint();

  const { data, error } = await supabase.rpc('claim_referral', {
    p_code: code.trim().toUpperCase(),
    p_device_fingerprint: fingerprint,
  });

  if (error) {
    console.warn('[referral] claim_referral RPC error', error);
    return { success: false, reason: 'rpc_error' };
  }

  const result = (data ?? {}) as {
    success: boolean;
    reason?: string;
    inviter_user_id?: string;
    note?: string;
  };

  return {
    success: result.success === true,
    reason: result.reason as ClaimResult['reason'],
    inviterUserId: result.inviter_user_id,
    note: result.note,
  };
}

/** Per-inviter summary used in the Invite Friends modal. */
export async function getMySummary(): Promise<ReferralSummary | null> {
  const { data, error } = await supabase
    .from('referral_summary')
    .select('*')
    .maybeSingle();
  if (error) {
    console.warn('[referral] referral_summary read failed', error);
    return null;
  }
  return (data as ReferralSummary | null) ?? null;
}

/** Canonical short share URL: https://pokerblitzgame.com/i/<code> */
export function buildShareUrl(code: string): string {
  return `${MARKETING_URL}/i/${encodeURIComponent(code)}`;
}

/**
 * Extract a referral code from any URL we may have been launched with.
 * Recognized shapes:
 *   - /i/<CODE>                         — short share URL
 *   - ?ref=<CODE>                       — query param fallback
 *   - https://anything/i/<CODE>?...     — full link with extras
 */
export function extractCodeFromUrl(url: string): string | null {
  try {
    const u = new URL(url);
    const refQ = u.searchParams.get('ref');
    if (refQ && refQ.length > 0) return refQ.toUpperCase();
    const match = /\/i\/([A-Z0-9]{4,12})\b/i.exec(u.pathname);
    if (match) return match[1].toUpperCase();
  } catch {
    // Not a valid URL — treat as bare code if it looks like one.
    if (/^[A-Z0-9]{4,12}$/i.test(url.trim())) return url.trim().toUpperCase();
  }
  return null;
}

/**
 * Persist a code seen at URL-launch time so we can claim it AFTER sign-in
 * (the user may visit the landing page first, then auth). Called by the
 * deep-link handler.
 */
export function rememberPendingCode(code: string): void {
  if (!code) return;
  try {
    localStorage.setItem(STORAGE_PENDING_CODE, code.toUpperCase());
  } catch {
    /* localStorage may be disabled — best effort */
  }
}

export function readPendingCode(): string | null {
  try {
    const v = localStorage.getItem(STORAGE_PENDING_CODE);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

export function clearPendingCode(): void {
  try {
    localStorage.removeItem(STORAGE_PENDING_CODE);
  } catch {
    /* swallow */
  }
}

/**
 * Best-effort device fingerprint. Stored permanently in localStorage so the
 * value persists across launches. Not used for blocking — analyst-only signal
 * to spot suspicious clusters (one device → many "different" inviters).
 */
function getOrMintDeviceFingerprint(): string {
  try {
    const existing = localStorage.getItem(STORAGE_DEVICE_FINGERPRINT);
    if (existing) return existing;
    const fp = `dev_${crypto.randomUUID()}`;
    localStorage.setItem(STORAGE_DEVICE_FINGERPRINT, fp);
    return fp;
  } catch {
    return 'unknown';
  }
}
